import { useRef, useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { toast } from "sonner";

// Turn a relative path like "/api/uploads/file/..." into a full URL if needed
export function resolveUploadUrl(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  const base = API.replace(/\/api$/, "");
  return url.startsWith("/api") ? `${base}${url}` : `${API}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function ImageUploader({ onUploaded, label = "Upload image", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = resolveUploadUrl(data.url);
      onUploaded(fullUrl);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={onFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testId}-btn`}
        className="rounded-full"
      >
        <Upload className="h-4 w-4 mr-2" /> {busy ? "Uploading…" : label}
      </Button>
    </>
  );
}
